<script lang="ts">
	import { getClient, mutate } from 'svelte-apollo';
	import Textfield from '@smui/textfield/styled';
	import FormField from '@smui/form-field/styled';
	import Radio from '@smui/radio/styled';
	import Button, { Label } from '@smui/button/styled';
	import { createForm } from 'svelte-forms-lib';
	import * as yup from 'yup';
	import { graphQLErrors as graphQLErrorsStore, userInfo } from '../../stores';
	import Dialog, { Title, Content, Actions } from '@smui/dialog/styled';
	import { Link } from 'svelte-routing';
	import { APP_PATHS } from '../../constants';
	import AppContainer from '../../components/AppContainer.svelte';
	import Text from '../../components/Text.svelte';
	import { createUser } from '../../graphql/queries/users';

	const client = getClient();

	let errorMessage: string;
	let errorDialog = false;
	let okDialog = false;

	const createUserOp = (values: { userEmail: string; userRole: string }) => {
		const { userEmail, userRole } = values;
		return new Promise((resolve) =>
			mutate(client, {
				mutation: createUser,
				variables: {
					email: userEmail,
					role: userRole
				},
				errorPolicy: 'all'
			})
				.then((dataResponse) => {
					resolve(dataResponse);
				})
				.catch((err) => {
					console.warn(err);
					resolve({ errors: err });
				})
		);
	};

	function handleError(errMsg: string) {
		errorMessage = errMsg;
		errorDialog = true;
		console.error('Error during creating of user', errMsg);
	}

	const { form, handleSubmit, isValid } = createForm({
		initialValues: {
			userEmail: '',
			userRole: 'user'
		},
		validationSchema: yup.object().shape({
			userEmail: yup.string().email().required(),
			userRole: yup.string().required()
		}),
		onSubmit: async (values) => {
			graphQLErrorsStore.set(undefined);
			return createUserOp(values)
				.then(() => {
					okDialog = true;
				})
				.catch((err) => {
					handleError(err.message);
				});
		}
	});
</script>

<AppContainer>
	<Text gutter type="headline3">Add new user</Text>

	<div class="description">
		<Text>Please fill user’s e-mail and role for new user</Text>
	</div>

	<form class:valid={$isValid} on:submit={handleSubmit}>
		<label for="userEmail">
			<Text type="subtitle1">User’s e-mail:</Text>
		</label>
		<Textfield
			class="fullwidth"
			variant="outlined"
			value={$form.userEmail}
			input$id="userEmail"
			input$type="email"
			on:change={(e) => {
				$form.userEmail = e.target.value;
			}}
		/>
		<Text type="subtitle1">Role:</Text>
		<div>
			<FormField>
				<Radio bind:group={$form.userRole} value={'user'} />
				<span slot="label">User</span>
			</FormField>
			<FormField>
				<Radio bind:group={$form.userRole} value={'admin'} />
				<span slot="label">Admin</span>
			</FormField>
			{#if $userInfo.role === 'SUPERADMIN'}
				<FormField>
					<Radio bind:group={$form.userRole} value={'superadmin'} />
					<span slot="label">Superadmin</span>
				</FormField>
			{/if}
		</div>

		<Button variant="raised" class="add-button">
			<Label>Send</Label>
		</Button>
	</form>
</AppContainer>

<Dialog
	open={okDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">User was sucessfully created</Title>
	<Content id="dialog-content">
		You can view it in
		<Link to={APP_PATHS.USER_MANAGEMENT}>User management section</Link>.
	</Content>

	<Actions>
		<Button on:click={() => (okDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<Dialog
	open={errorDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Error during creating the user</Title>
	<Content id="dialog-content">{errorMessage}</Content>

	<Actions>
		<Button on:click={() => (errorDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<style>
	:global(.description) {
		margin-bottom: 35px;
	}

	:global(.add-button) {
		margin-top: 30px;
	}
</style>
